// react
import React from 'react';

// mui
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardProps,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { Squiggle } from './Squiggle';

export type SquiggleColor = 'cherry' | 'lime' | 'warmOrange';

export interface SquiggleCardAction {
  label: string;
  onClick: () => void;
  /** Override button style. Primary defaults to 'contained', secondary to 'outlined'. */
  variant?: 'contained' | 'outlined';
}

export interface SquiggleCardProps extends Omit<
  CardProps,
  'title' | 'variant'
> {
  squiggleColor: SquiggleColor;
  squiggleFlipped?: boolean;
  /** Show the decorative squiggle. Defaults to true. */
  squiggle?: boolean;
  /** 'default' uses h2 title and full padding; 'compact' uses h5 title, h6 subtitle, and tighter padding. */
  variant?: 'default' | 'compact';
  title: string;
  subtitle?: string;
  /** Optional element rendered to the left of the title (e.g., an Avatar). */
  avatar?: React.ReactNode;
  progress?: number;
  chevron?: boolean;
  primaryAction?: SquiggleCardAction;
  secondaryAction?: SquiggleCardAction;
}

/**
 * SquiggleCard is a Card with a decorative squiggle, a title/subtitle header,
 * and 0–2 CTA buttons. Built on top of CardHeader + CardActions so it shares
 * the same primitives as any other MUI Card layout.
 */
export const SquiggleCard = ({
  squiggleColor,
  squiggleFlipped,
  squiggle = true,
  variant = 'default',
  title,
  subtitle,
  avatar,
  progress,
  chevron,
  primaryAction,
  secondaryAction,
  sx,
  ...cardProps
}: SquiggleCardProps) => {
  const theme = useTheme();
  const tintMap: Record<SquiggleColor, string> = {
    cherry: theme.palette.custom.cherry[50],
    lime: theme.palette.custom.limeGreen[100],
    warmOrange: theme.palette.custom.warmOrange[100],
  };
  const fill = tintMap[squiggleColor];
  const hasActions = Boolean(primaryAction || secondaryAction);
  const hasProgress = typeof progress === 'number';
  const isCompact = variant === 'compact';

  return (
    <Card
      {...cardProps}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        cursor: cardProps.onClick ? 'pointer' : 'default',
        ...sx,
      }}
    >
      {squiggle && (
        <Box
          sx={{
            position: 'absolute',
            top: -540,
            right: -540,
            width: 1400,
            height: 1400 * (228.3 / 613.555),
            transform: squiggleFlipped
              ? 'scaleX(-1) rotate(135deg)'
              : 'rotate(135deg)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <Squiggle color={fill} />
        </Box>
      )}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <CardHeader
          avatar={avatar}
          title={title}
          subheader={subtitle}
          action={chevron ? <Icon icon='chevron-right' /> : undefined}
          sx={
            isCompact
              ? {
                  py: 2.5,
                  px: 2.5,
                  '& .MuiCardHeader-action': {
                    ml: 2,
                    mr: 0,
                    alignSelf: 'center',
                  },
                }
              : undefined
          }
          slotProps={{
            title: isCompact ? { variant: 'h5' } : undefined,
            subheader: isCompact ? { variant: 'h6' } : undefined,
          }}
        />
        {hasProgress && (
          <CardContent
            sx={
              isCompact
                ? { pt: 0, pb: 2.5, px: 2.5, '&:last-child': { pb: 2.5 } }
                : { pt: 0 }
            }
          >
            {isCompact ? (
              <LinearProgress variant='determinate' value={progress} />
            ) : (
              <Stack
                direction='row'
                spacing={2}
                sx={{
                  alignItems: 'center',
                }}
              >
                <LinearProgress
                  variant='determinate'
                  value={progress}
                  sx={{ flex: 1 }}
                />
                <Typography variant='caption'>
                  {Math.round(progress)}%
                </Typography>
              </Stack>
            )}
          </CardContent>
        )}
        {hasActions && (
          <Stack spacing={2} sx={{ p: 2, pt: 0 }}>
            {primaryAction && (
              <Button
                variant={primaryAction.variant ?? 'contained'}
                fullWidth
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant={secondaryAction.variant ?? 'outlined'}
                fullWidth
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Card>
  );
};

SquiggleCard.displayName = 'SquiggleCard';
export default SquiggleCard;
